import { useState, useRef, useEffect, useCallback } from 'react';
import type { AgreementDoc } from '../agreementContent';

interface AgreementModalProps {
  docs: AgreementDoc[];
  onConfirm: () => void;
  onCancel?: () => void;
  busy?: boolean;
  error?: string;
}

const CHECKBOXES = [
  '我已阅读并理解上述所有协议和风险告知书的全部内容',
  '我理解外汇、差价合约及杠杆交易存在极高风险，过往业绩不代表未来表现，我可能损失全部本金',
  '我确认具备独立判断能力，知悉平台所有 AI 分析、信号、建议仅供参考，不构成投资建议或交易指令',
  '我已知悉所在地法律法规对差价合约(CFD)、外汇保证金、杠杆交易等金融产品的监管要求，并确认我的使用行为完全符合当地法律（AC-LEGAL-04）',
];

export default function AgreementModal({ docs, onConfirm, onCancel, busy = false, error }: AgreementModalProps) {
  const [step, setStep] = useState<'list' | 'reading' | 'confirm'>('list');
  const [readingIndex, setReadingIndex] = useState(0);
  const [readDocIds, setReadDocIds] = useState<Set<string>>(new Set());
  const [checks, setChecks] = useState<boolean[]>([false, false, false, false]);
  const [reachedBottom, setReachedBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentDoc = docs[readingIndex];
  const allRead = docs.every(d => readDocIds.has(d.id));
  const allChecked = checks.every(Boolean);

  // Reset per-doc scroll state
  useEffect(() => {
    setReachedBottom(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [readingIndex, step]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setReachedBottom(true);
    }
  }, []);

  const markDocRead = (id: string) => {
    setReadDocIds(prev => new Set(prev).add(id));
  };

  const openDoc = (idx: number) => {
    setReadingIndex(idx);
    setStep('reading');
    setReachedBottom(false);
  };

  const finishReading = () => {
    if (currentDoc) markDocRead(currentDoc.id);
    setStep('list');
  };

  const toggleCheck = (idx: number) => {
    setChecks(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handleFinalConfirm = () => {
    if (!allChecked || busy) return;
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#F4F6FA] flex flex-col">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-100 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center px-4 h-12">
          {step !== 'list' && (
            <button
              onClick={() => setStep(step === 'reading' ? 'list' : 'list')}
              className="w-8 h-8 flex items-center justify-center"
            >
              <i className="ri-arrow-left-s-line text-gray-600 text-lg" />
            </button>
          )}
          <div className="flex-1 text-center min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">
              {step === 'list' && '协议与风险告知'}
              {step === 'reading' && currentDoc?.title}
              {step === 'confirm' && '最终确认'}
            </h2>
          </div>
          {onCancel ? <button type="button" onClick={onCancel} className="min-h-11 text-sm text-indigo-600">取消</button> : <div className="w-8" />}
        </div>
      </div>

      {error && <p role="alert" className="px-5 py-3 text-sm text-red-600">{error}</p>}
      {/* ── LIST step ── */}
      {step === 'list' && (
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <p className="text-gray-500 text-xs mb-4 leading-relaxed">
            使用 ForexMind 前，您需要阅读并理解以下协议与风险告知书。请点击每项进入详细阅读。
          </p>

          <div className="flex flex-col gap-3">
            {docs.map((doc, idx) => {
              const isRead = readDocIds.has(doc.id);
              return (
                <button
                  key={doc.id}
                  onClick={() => openDoc(idx)}
                  className="w-full text-left bg-white rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: isRead ? '#ECFDF5' : '#F3F4F6' }}
                  >
                    <i
                      className={`${isRead ? 'ri-checkbox-circle-fill' : 'ri-file-text-line'} text-lg`}
                      style={{ color: isRead ? '#10B981' : '#9CA3AF' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-semibold text-sm">{doc.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{doc.clauseCount ?? doc.sections.length} 项条款</p>
                  </div>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                      color: isRead ? '#059669' : '#9CA3AF',
                      background: isRead ? '#D1FAE5' : '#F3F4F6',
                    }}
                  >
                    {isRead ? '已阅读' : '未阅读'}
                  </span>
                  <i className="ri-arrow-right-s-line text-gray-300 text-lg flex-shrink-0" />
                </button>
              );
            })}
          </div>

          {/* Final confirm button */}
          <div className="mt-6">
            <button
              onClick={() => allRead && setStep('confirm')}
              disabled={!allRead}
              className="w-full font-semibold text-sm flex-shrink-0"
              style={{
                height: '52px',
                minHeight: '52px',
                borderRadius: '16px',
                background: allRead
                  ? 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)'
                  : '#E5E7EB',
                color: allRead ? '#FFFFFF' : '#9CA3AF',
                cursor: allRead ? 'pointer' : 'not-allowed',
                border: 'none',
                padding: 0,
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center gap-2">
                <i className={`${allRead ? 'ri-check-double-line' : 'ri-lock-line'} text-base`} />
                {allRead ? '进入最终确认' : `还有 ${docs.length - readDocIds.size} 项未阅读`}
              </span>
            </button>
          </div>

          <div className="h-6" />
        </div>
      )}

      {/* ── READING step ── */}
      {step === 'reading' && currentDoc && (
        <>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto px-5 py-5"
          >
            <div
              className="bg-white rounded-2xl p-5"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-[#635BFF] text-xs font-medium">更新于 {currentDoc.updatedAt}</p>
                {!reachedBottom && (
                  <span className="text-[11px] text-amber-500 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                    请阅读至底部
                  </span>
                )}
                {reachedBottom && (
                  <span className="text-[11px] text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
                    已阅读完毕
                  </span>
                )}
              </div>

              {currentDoc.sections.map((sec, i) => (
                <div key={i} className="mb-5">
                  <h4 className="text-gray-800 font-bold text-sm mb-2">{sec.heading}</h4>
                  <p
                    className="text-gray-500 text-xs leading-relaxed"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {sec.body}
                  </p>
                </div>
              ))}
            </div>
            <div className="h-6" />
          </div>

          {/* Bottom sticky bar */}
          <div className="flex-shrink-0 px-5 py-3 bg-white border-t border-gray-100">
            <button
              onClick={finishReading}
              disabled={!reachedBottom}
              className="w-full font-semibold text-sm flex-shrink-0"
              style={{
                height: '52px',
                minHeight: '52px',
                borderRadius: '16px',
                background: reachedBottom
                  ? 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)'
                  : '#E5E7EB',
                color: reachedBottom ? '#FFFFFF' : '#9CA3AF',
                cursor: reachedBottom ? 'pointer' : 'not-allowed',
                border: 'none',
                padding: 0,
                WebkitAppearance: 'none',
                appearance: 'none',
                display: 'block',
                position: 'relative',
              }}
            >
              <span className="absolute inset-0 flex items-center justify-center gap-2">
                <i className={`${reachedBottom ? 'ri-check-line' : 'ri-lock-line'} text-base`} />
                {reachedBottom ? '我已阅读并同意' : '请阅读至底部'}
              </span>
            </button>
          </div>
        </>
      )}

      {/* ── CONFIRM step ── */}
      {step === 'confirm' && (
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="bg-white rounded-2xl p-5 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-amber-500 text-sm" />
              </div>
              <p className="text-gray-900 font-semibold text-sm">风险确认声明</p>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              在继续使用 ForexMind 之前，请确认您已充分理解以下声明。只有全部勾选后才能继续注册/登录。
            </p>

            <div className="flex flex-col gap-3">
              {CHECKBOXES.map((label, idx) => (
                <button
                  key={idx}
                  onClick={() => toggleCheck(idx)}
                  className="flex items-start gap-3 text-left w-full"
                >
                  <div
                    className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 border transition-colors"
                    style={{
                      borderColor: checks[idx] ? '#6366f1' : '#D1D5DB',
                      background: checks[idx] ? '#6366f1' : '#FFFFFF',
                    }}
                  >
                    {checks[idx] && <i className="ri-check-line text-white text-xs" />}
                  </div>
                  <span className="text-gray-700 text-xs leading-relaxed">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleFinalConfirm}
            disabled={!allChecked || busy}
            className="w-full font-semibold text-sm flex-shrink-0"
            style={{
              height: '52px',
              minHeight: '52px',
              borderRadius: '16px',
              background: allChecked
                ? 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)'
                : '#E5E7EB',
              color: allChecked ? '#FFFFFF' : '#9CA3AF',
              cursor: allChecked ? 'pointer' : 'not-allowed',
              border: 'none',
              padding: 0,
              WebkitAppearance: 'none',
              appearance: 'none',
              display: 'block',
              position: 'relative',
            }}
          >
            <span className="absolute inset-0 flex items-center justify-center gap-2">
              <i className={`${allChecked ? 'ri-check-double-line' : 'ri-lock-line'} text-base`} />
              {busy ? '正在确认…' : allChecked ? '确认并继续' : `还有 ${checks.filter(c => !c).length} 项未勾选`}
            </span>
          </button>

          <div className="h-6" />
        </div>
      )}
    </div>
  );
}
